import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { authClient } from "@/lib/auth-client";
import { api } from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertCircle, CheckCircle2, Loader2, LogOut } from "lucide-react";
import { toast } from "sonner";
import { InvitationHeader } from "./components/invitation-header";
import { InvitationLoginForm } from "./components/invitation-login-form";
import { InvitationSignupForm } from "./components/invitation-signup-form";

type PageState =
  | "loading"
  | "error"
  | "expired"
  | "already_used"
  | "wrong_email"
  | "accepting"
  | "login"
  | "signup"
  | "success";

export function AcceptInvitationPage() {
  const { id: invitationId } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // For states that can only be set by async operations
  const [asyncState, setAsyncState] = useState<"accepting" | "success" | "error" | null>(null);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const hasStartedAutoAccept = useRef(false);

  const { data: session, isPending: isSessionLoading } = authClient.useSession();

  // Fetch invitation info
  const {
    data: invitationInfo,
    isLoading: isInvitationLoading,
    error: invitationError,
  } = api.useQuery("get", "/invitations/{id}/public", {
    params: { path: { id: invitationId! } },
  });

  // Compute page state based on data (no useState needed for derived state)
  const computedState = useMemo((): PageState => {
    // If we have an async state override, use it
    if (asyncState) return asyncState;

    // Loading
    if (isInvitationLoading || isSessionLoading) {
      return "loading";
    }

    // Error fetching invitation
    if (invitationError || !invitationInfo) {
      return "error";
    }

    // API returned an error
    if ("error" in invitationInfo) {
      return "error";
    }

    // Expired
    if (invitationInfo.isExpired) {
      return "expired";
    }

    // Already used
    if (invitationInfo.status !== "pending") {
      return "already_used";
    }

    // User is logged in
    if (session?.user) {
      // Wrong email
      if (session.user.email !== invitationInfo.email) {
        return "wrong_email";
      }
      // Will auto-accept
      return "accepting";
    }

    // Not logged in
    return invitationInfo.userExists ? "login" : "signup";
  }, [asyncState, isInvitationLoading, isSessionLoading, invitationError, invitationInfo, session]);

  // Get error message for display
  const displayErrorMessage = useMemo(() => {
    if (errorMessage) return errorMessage;
    if (invitationError) return "Invitation non trouvée";
    if (invitationInfo && "error" in invitationInfo) {
      const errorData = invitationInfo as { error: string; message?: string };
      return errorData.message || "Erreur inconnue";
    }
    return "Une erreur est survenue";
  }, [errorMessage, invitationError, invitationInfo]);

  // Auto-accept invitation for logged-in users
  const handleAutoAccept = useCallback(async () => {
    if (!invitationId || !invitationInfo || "error" in invitationInfo) return;

    try {
      const result = await authClient.organization.acceptInvitation({
        invitationId,
      });

      if (result.error) {
        toast.error("Erreur lors de l'acceptation", {
          description: result.error.message,
        });
        setAsyncState("error");
        setErrorMessage(result.error.message || "Erreur lors de l'acceptation");
        return;
      }

      // Set as active organization
      const invitations = await authClient.organization.listInvitations({});
      const acceptedInvitation = invitations.data?.find((inv) => inv.id === invitationId);

      if (acceptedInvitation?.organizationId) {
        await authClient.organization.setActive({
          organizationId: acceptedInvitation.organizationId,
        });
      }

      setAsyncState("success");
      toast.success("Invitation acceptée !");

      // Redirect after a short delay
      setTimeout(() => {
        navigate("/dash", { replace: true });
      }, 1500);
    } catch (error) {
      toast.error("Erreur inattendue", {
        description: (error as Error).message,
      });
      setAsyncState("error");
      setErrorMessage((error as Error).message);
    }
  }, [invitationId, invitationInfo, navigate]);

  // Trigger auto-accept when computed state is "accepting"
  // Using a ref to track if we've started and calling the async function
  // The setState calls happen in the async callback, not synchronously in the effect
  useEffect(() => {
    if (computedState === "accepting" && !hasStartedAutoAccept.current) {
      hasStartedAutoAccept.current = true;
      // Schedule the async operation outside the effect's synchronous execution
      void (async () => {
        await handleAutoAccept();
      })();
    }
  }, [computedState, handleAutoAccept]);

  // Handle successful login/signup
  const handleSuccess = useCallback(
    async (organizationId: string) => {
      if (organizationId) {
        await authClient.organization.setActive({ organizationId });
      }

      setAsyncState("success");
      toast.success("Bienvenue dans le club !");

      setTimeout(() => {
        navigate("/dash", { replace: true });
      }, 1500);
    },
    [navigate]
  );

  // Handle errors from forms
  const handleError = useCallback((message: string) => {
    toast.error("Erreur", { description: message });
  }, []);

  // Handle logout for wrong email case
  const handleLogout = useCallback(async () => {
    await authClient.signOut();
    // Page will re-render and show login/signup form
  }, []);

  // Loading state
  if (computedState === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <Skeleton className="mx-auto h-8 w-48" />
            <Skeleton className="mx-auto mt-2 h-4 w-32" />
          </CardHeader>
          <CardContent className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error states
  if (computedState === "error" || computedState === "expired" || computedState === "already_used") {
    const errorContent = {
      error: {
        title: "Invitation invalide",
        description: displayErrorMessage,
      },
      expired: {
        title: "Invitation expirée",
        description: "Cette invitation a expiré. Veuillez demander une nouvelle invitation.",
      },
      already_used: {
        title: "Invitation déjà utilisée",
        description: "Cette invitation a déjà été acceptée ou annulée.",
      },
    };

    const content = errorContent[computedState];

    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10">
              <AlertCircle className="h-6 w-6 text-destructive" />
            </div>
            <CardTitle>{content.title}</CardTitle>
            <CardDescription>{content.description}</CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="outline" className="w-full" onClick={() => navigate("/login", { replace: true })}>
              Retour à la connexion
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Wrong email state
  if (computedState === "wrong_email" && invitationInfo && !("error" in invitationInfo)) {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-yellow-100">
              <AlertCircle className="h-6 w-6 text-yellow-600" />
            </div>
            <CardTitle>Email incorrect</CardTitle>
            <CardDescription>
              Cette invitation est destinée à <strong>{invitationInfo.emailRedacted}</strong>.
              <br />
              Vous êtes connecté avec <strong>{session?.user?.email}</strong>.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <Button variant="outline" className="w-full" onClick={() => navigate("/dash", { replace: true })}>
              Aller au dashboard
            </Button>
            <Button variant="ghost" className="w-full" onClick={handleLogout}>
              <LogOut className="mr-2 h-4 w-4" />
              Se déconnecter
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Accepting state (auto-accept in progress)
  if (computedState === "accepting") {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
            <CardTitle>Acceptation en cours...</CardTitle>
            <CardDescription>Veuillez patienter pendant que nous vous ajoutons au club.</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // Success state
  if (computedState === "success") {
    return (
      <div className="flex min-h-screen items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <CheckCircle2 className="h-6 w-6 text-green-600" />
            </div>
            <CardTitle>Bienvenue !</CardTitle>
            <CardDescription>Vous avez rejoint le club avec succès. Redirection en cours...</CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  // Login or Signup form
  if (!invitationInfo || "error" in invitationInfo) {
    return null;
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <InvitationHeader
          organizationName={invitationInfo.organizationName}
          inviterName={invitationInfo.inviterName}
          subtitle={computedState === "login" ? "Connectez-vous pour rejoindre le club" : "Créez votre compte pour rejoindre le club"}
        />
        <CardContent>
          {computedState === "login" ? (
            <InvitationLoginForm
              email={invitationInfo.email}
              emailRedacted={invitationInfo.emailRedacted}
              invitationId={invitationId!}
              onSuccess={handleSuccess}
              onError={handleError}
            />
          ) : (
            <InvitationSignupForm
              email={invitationInfo.email}
              emailRedacted={invitationInfo.emailRedacted}
              invitationId={invitationId!}
              onSuccess={handleSuccess}
              onError={handleError}
            />
          )}
        </CardContent>
      </Card>
    </div>
  );
}
